/**
 * Elegant error message translation utility
 * Maps error messages directly to existing translation keys in en.json
 */

/**
 * Direct mapping from error messages/codes to translation keys in en.json
 * This ensures we use the exact keys that exist in the localization file
 */
const ERROR_MESSAGE_TO_TRANSLATION_KEY: Record<string, string> = {
  // Contact validation errors
  'contact-invalid-name-and-phone-number':
    'orderIntake.rmc.contactValidation.contactInvalidNameAndPhoneNumber',
  'contact-invalid-phone-number': 'orderIntake.rmc.contactValidation.contactInvalidPhoneNumber',
  'contact-invalid-name': 'orderIntake.rmc.contactValidation.contactInvalidName',
  'contact-phone-number-required': 'orderIntake.rmc.contactValidation.contactPhoneNumberRequired',
  'contact-name-required': 'orderIntake.rmc.contactValidation.contactNameRequired',

  // Field validation errors
  'poNumber-required': 'orderIntake.rmc.fieldValidation.poNumberRequired',
  'additionalNotes-required-max-length': 'orderIntake.rmc.fieldValidation.additionalNotesMaxLength'
}

/**
 * Enhanced error message helper for form validation
 * @param error - Error object from form validation
 * @param t - Translation function from useTranslation hook
 * @returns Translated error message or empty string
 */
export const getTranslatedErrorMessage = (error: unknown, t: (key: string) => string): string => {
  if (!error || typeof error !== 'object') return ''

  const errorObj = error as {message?: string; type?: string; [key: string]: unknown}

  // Try to get translation key from error type first
  if (errorObj.type) {
    const translationKey = ERROR_MESSAGE_TO_TRANSLATION_KEY[errorObj.type]
    if (translationKey) {
      const translatedMessage = t(translationKey)
      // If translation exists (not the same as the key), use it
      if (translatedMessage !== translationKey) {
        return translatedMessage
      }
    }
  }

  // If no type or type didn't match, try the message
  if (errorObj.message) {
    const translationKey = ERROR_MESSAGE_TO_TRANSLATION_KEY[errorObj.message]
    if (translationKey) {
      const translatedMessage = t(translationKey)
      // If translation exists (not the same as the key), use it
      if (translatedMessage !== translationKey) {
        return translatedMessage
      }
    }
  }

  // Fallback to empty string if no translation found
  return ''
}
